import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';

export const Header = ({ onOpenLeadModal }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { getCartCount } = useCart();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/pavers', label: t('nav.pavers') },
    { path: '/brick-colors', label: t('nav.brickColors') },
    { path: '/remodeling', label: t('nav.remodeling') },
    { path: '/projects', label: t('nav.projects') },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') },
    { path: '/shop', label: t('nav.shop') }
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-[hsl(215,75%,16%)] shadow-lg"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/66uykm11_logo%20edx%20para%20site.png" 
              alt="EDX Construction" 
              className="h-16 w-auto sm:h-20"
              style={{ maxWidth: '200px' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-full text-sm font-semibold uppercase tracking-wide transition-all duration-200 ${
                  location.pathname === link.path
                    ? 'text-white bg-[hsl(224,64%,33%)]'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`nav-link-${link.path.slice(1) || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {/* CTA Button */}
            <Button
              onClick={onOpenLeadModal}
              className="hidden md:inline-flex btn-premium btn-premium-secondary"
              data-testid="header-cta-button"
            >
              {t('hero.cta')}
            </Button>

            {/* Cart Icon */}
            <button
              onClick={() => navigate('/cart')}
              className="relative p-2 text-white hover:bg-white/10 rounded-full transition-colors duration-200"
              data-testid="header-cart-button"
            >
              <ShoppingCart className="w-6 h-6" />
              {getCartCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-[hsl(224,64%,33%)] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-white hover:bg-white/10"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-[hsl(215,75%,16%)] border-t border-white/10" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-md text-sm font-semibold uppercase tracking-wide transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-white bg-[hsl(224,64%,33%)]'
                    : 'text-white/80 hover:bg-white/10'
                }`}
                data-testid={`mobile-nav-link-${link.path.slice(1) || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-white/10">
              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onOpenLeadModal();
                }}
                className="w-full btn-premium btn-premium-secondary"
                data-testid="mobile-cta-button"
              >
                {t('hero.cta')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};